import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wraps a click handler with a guaranteed-visible pending state.
 *
 * Bug 2026-08-04: pause/resume click gave no visible feedback on poor
 * networks, so users clicked repeatedly thinking nothing was happening.
 * The Redux dispatch is synchronous but the underlying transport (tus
 * abort/start + backend notify) can hang for seconds on a weak link.
 *
 * Guarantees:
 *   - pending flips true synchronously on click, so the button visibly
 *     changes state in the same frame the click is registered
 *   - pending stays true for at least `minVisibleMs` (default 500 ms)
 *     even if `settled` becomes true earlier, so a sub-frame settle
 *     doesn't produce an invisible flash
 *   - pending clears as soon as EITHER (a) `settled` is true AND
 *     minVisibleMs elapsed, OR (b) `maxWaitMs` elapsed — the safety net
 *     so a permanently-hung RPC doesn't leave the spinner spinning
 *     forever
 *   - double-clicks while pending are ignored (only one dispatch per
 *     pending window)
 *
 * @param {() => void} action                 the sync/async function to invoke on trigger
 * @param {object} [options]
 * @param {number} [options.minVisibleMs=500] minimum spinner visibility window
 * @param {number} [options.maxWaitMs=5000]   hard timeout so a hung action isn't a stuck spinner
 * @param {boolean} [options.settled]         external signal that the underlying operation completed
 * @returns {{ pending: boolean, trigger: () => void }}
 */
export const usePendingClick = (action, options = {}) => {
  const {
    minVisibleMs = 500,
    maxWaitMs = 5000,
    settled = false,
  } = options;

  const [pending, setPending] = useState(false);
  const startedAtRef = useRef(0);
  const timeoutRef = useRef(null);

  const clearPendingAfter = useCallback((delayMs) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setPending(false);
      timeoutRef.current = null;
    }, Math.max(0, delayMs));
  }, []);

  const trigger = useCallback(() => {
    if (pending) return; // ignore double-click
    startedAtRef.current = Date.now();
    setPending(true);
    // Safety net — never leave the spinner spinning forever if the caller's
    // `settled` never flips (e.g. hung network request that no state
    // transition observes).
    clearPendingAfter(maxWaitMs);
    try {
      action();
    } catch (_error) {
      // Swallow — the spinner still shows so the user has feedback that
      // the click was received; the action's error path is expected to
      // surface any user-facing message via its own alert channel.
    }
  }, [pending, action, maxWaitMs, clearPendingAfter]);

  // When the caller signals settled=true, clear pending — but respect the
  // minVisibleMs guarantee. If minVisibleMs hasn't elapsed yet, schedule
  // the clear for the remainder of the window.
  useEffect(() => {
    if (!pending) return;
    if (!settled) return;
    const elapsed = Date.now() - startedAtRef.current;
    const remaining = Math.max(0, minVisibleMs - elapsed);
    clearPendingAfter(remaining);
  }, [pending, settled, minVisibleMs, clearPendingAfter]);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  return { pending, trigger };
};
